import { useMemo, useState } from "react";
import { rankingFilms, rankingGenres } from "@/mocks/films";
import SectionHeading from "./SectionHeading";

export default function RankingSection() {
  const [genre, setGenre] = useState("全部");
  const [query, setQuery] = useState("");

  const keyword = query.trim().toLowerCase();
  const isFiltering = genre !== "全部" || keyword !== "";

  const filtered = useMemo(() => {
    return rankingFilms.filter((film) => {
      const matchGenre = genre === "全部" || film.genre === genre;
      const haystack = [
        film.title,
        film.original,
        film.director,
        film.genre,
        film.tags.join(" "),
        film.summary,
      ]
        .join(" ")
        .toLowerCase();
      const matchKeyword = keyword === "" || haystack.includes(keyword);
      return matchGenre && matchKeyword;
    });
  }, [genre, keyword]);

  const featured = rankingFilms.filter((film) => film.image !== "");
  const listFilms = rankingFilms.filter((film) => film.image === "");

  return (
    <section id="ranking" className="border-b border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="编辑排行榜"
          title="韩国伦理电影推荐排行榜 Top 10"
          desc="榜单综合叙事完成度、表演、影像风格与议题深度排序，分数是编辑部的观感参考。想缩小范围，就用下面的题材筛选和关键词搜索。"
        />

        <div className="mt-7 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {rankingGenres.map((item) => {
              const active = item === genre;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => setGenre(item)}
                  className={
                    active
                      ? "whitespace-nowrap rounded-full bg-secondary-500 px-4 py-1.5 text-sm font-label text-background-50"
                      : "whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm font-label text-foreground-700 transition-colors hover:bg-background-200"
                  }
                >
                  {item}
                </button>
              );
            })}
          </div>

          <div className="flex w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 lg:w-72">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索片名 / 导演 / 关键词"
              aria-label="搜索片单"
              className="w-full bg-transparent text-sm font-body text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            {query !== "" && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => setQuery("")}
                className="flex h-5 w-5 items-center justify-center text-foreground-400 hover:text-foreground-700"
              >
                <i className="ri-close-circle-line text-base"></i>
              </button>
            )}
          </div>
        </div>

        {!isFiltering ? (
          <div className="mt-8">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {featured.map((film) => (
                <article
                  key={film.id}
                  className="flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
                >
                  <div className="relative h-64 w-full overflow-hidden">
                    <img
                      src={film.image}
                      alt={`《${film.title}》影片氛围示意`}
                      className="h-full w-full object-cover object-top"
                    />
                    <span className="absolute left-3 top-3 flex h-8 items-center gap-1 rounded-full bg-background-50/90 px-3 font-heading text-sm font-black text-accent-700">
                      <i className="ri-medal-line"></i>
                      NO.{film.rank}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-heading text-lg font-bold text-foreground-950">
                          {film.title}
                        </h3>
                        <p className="mt-0.5 text-xs font-label text-foreground-500">
                          {film.original} · {film.year} · {film.director}
                        </p>
                      </div>
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-base font-black text-background-50">
                        {film.score}
                      </span>
                    </div>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">
                      {film.summary}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {film.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-label text-secondary-900"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <ul className="mt-6 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {listFilms.map((film) => (
                <li
                  key={film.id}
                  className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:gap-5 md:p-5"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-200 font-heading text-base font-black text-foreground-800">
                    {film.rank}
                  </span>
                  <div className="md:w-56 md:shrink-0">
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      {film.title}
                    </h3>
                    <p className="mt-0.5 text-xs font-label text-foreground-500">
                      {film.original} · {film.year} · {film.director}
                    </p>
                  </div>
                  <p className="flex-1 text-sm leading-relaxed text-foreground-600">
                    {film.summary}
                  </p>
                  <div className="flex shrink-0 items-center gap-3">
                    <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label text-foreground-700">
                      {film.genre}
                    </span>
                    <span className="font-heading text-lg font-black text-accent-600">
                      {film.score}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mt-8">
            <p className="text-sm font-label text-foreground-500">
              共找到 {filtered.length} 部符合条件作品
            </p>
            {filtered.length === 0 ? (
              <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <p className="mt-4 font-heading text-base font-bold text-foreground-800">
                  没有找到匹配的作品
                </p>
                <p className="mt-1 text-sm text-foreground-500">
                  试试换个关键词，或把题材切回“全部”。
                </p>
              </div>
            ) : (
              <ul className="mt-4 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                {filtered.map((film) => (
                  <li
                    key={film.id}
                    className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:gap-5 md:p-5"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-200 font-heading text-base font-black text-foreground-800">
                      {film.rank}
                    </span>
                    <div className="md:w-56 md:shrink-0">
                      <h3 className="font-heading text-base font-bold text-foreground-950">
                        {film.title}
                      </h3>
                      <p className="mt-0.5 text-xs font-label text-foreground-500">
                        {film.original} · {film.year} · {film.director}
                      </p>
                    </div>
                    <p className="flex-1 text-sm leading-relaxed text-foreground-600">
                      {film.summary}
                    </p>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label text-foreground-700">
                        {film.genre}
                      </span>
                      <span className="font-heading text-lg font-black text-accent-600">
                        {film.score}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}